import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: string;
  schema?: object | object[];
  keywords?: string;
  image?: string;
  noindex?: boolean;
}

const SITE_URL = 'https://www.dahlogsvane.dk';
const SITE_NAME = 'Dahl & Svane';
const DEFAULT_OG_IMAGE = '/og-image.png';

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  type = 'website',
  schema,
  keywords,
  image,
  noindex = false,
}) => {
  const fullTitle = `${title} | ${SITE_NAME}`;
  const url = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
  const ogImageUrl = image
    ? (image.startsWith('http') ? image : `${SITE_URL}${image}`)
    : `${SITE_URL}${DEFAULT_OG_IMAGE}`;

  // Normalise schema to an array so we can render multiple JSON-LD blocks
  const schemaArray = schema
    ? (Array.isArray(schema) ? schema : [schema])
    : [];

  return (
    <Helmet>
      {/* Standard SEO */}
      <html lang="da" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <link rel="canonical" href={url} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook / LinkedIn */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content="da_DK" />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} | ${title}`} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} | ${title}`} />

      {/* JSON-LD Structured Data for SEO + AEO (Answer Engine Optimization).
          Multiple schemas are rendered as separate <script> blocks so each
          remains valid JSON-LD. Accepts a single object or array of objects. */}
      {schemaArray.map((s, i) => (
        <script type="application/ld+json" key={`schema-${i}`}>
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};
